/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { moveItemInArray } from '@angular/cdk/drag-drop';
import type { ApiTypes, Loadable, Loaded } from '@hyundai/ng-common-lib';
import {
    AboutUsFeature,
    BusinessCustomerFeature,
    HomeFeature,
    ImprintPageFeature,
    PrivacyFeature,
    ServicesFeature,
    ShopPageFeature,
} from '@hyundai/ng-common-lib';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { actions } from './edit.actions';

export interface EditState {
    editModeEnabled: boolean;
    aboutUsTeasersDraft: ApiTypes.AboutUsTeaser[];
    aboutUsTeaserToRemove: number[];
    employees: ApiTypes.EditEmployee[];
    dealerCampaigns: Loadable<ApiTypes.DealerCampaignEdit[]>;
    dealerCampaign: Loadable<ApiTypes.DealerCampaignEditDetail>;
    textListBullets: ApiTypes.EditTextListBullet[];
    services: ApiTypes.EditContentList[];
    teasers: ApiTypes.HomeTeaserList[];
}

const initialState: EditState = {
    editModeEnabled: true,
    aboutUsTeasersDraft: [],
    aboutUsTeaserToRemove: [],
    employees: [],
    dealerCampaigns: null,
    dealerCampaign: null,
    textListBullets: [],
    services: [],
    teasers: [],
};

export const state = createFeature({
    name: 'edit',
    reducer: createReducer(
        initialState,
        on(
            actions.enableEditmode,
            (state): EditState => ({
                ...state,
                editModeEnabled: true,
            }),
        ),
        on(
            actions.disableEditmode,
            (state): EditState => ({
                ...state,
                editModeEnabled: false,
            }),
        ),
        on(
            actions.initAboutUsTeaserEdit,
            (state, { teasers }): EditState => ({
                ...state,
                aboutUsTeasersDraft: teasers,
                aboutUsTeaserToRemove: [],
            }),
        ),
        on(
            actions.addNewAboutUsTeaser,
            (state): EditState => ({
                ...state,
                aboutUsTeasersDraft: [
                    ...state.aboutUsTeasersDraft,
                    { id: -1, title: '', text: '', linkText: '', link: '', image: '', sorting: -1 },
                ],
            }),
        ),
        on(actions.removeAboutUsTeaser, (state, { index }): EditState => {
            const teaserOnIndex = state.aboutUsTeasersDraft[index];
            const aboutUsTeaserToRemove = [...state.aboutUsTeaserToRemove];
            if (teaserOnIndex.id !== -1) {
                aboutUsTeaserToRemove.push(teaserOnIndex.id);
            }
            return {
                ...state,
                aboutUsTeaserToRemove,
                aboutUsTeasersDraft: state.aboutUsTeasersDraft.filter((_, i) => i !== index),
            };
        }),
        on(
            actions.updateAboutUsTeaser,
            (state, { teaser }): EditState => ({
                ...state,
                aboutUsTeasersDraft: state.aboutUsTeasersDraft.map((t) => (t.id === teaser.id ? teaser : t)),
            }),
        ),
        on(
            actions.saveAboutUsTeasers,
            (state): EditState => ({
                ...state,
                aboutUsTeasersDraft: state.aboutUsTeasersDraft.map((t, sorting) => ({ ...t, sorting })),
            }),
        ),
        on(actions.updateAboutUsTeasersOrder, (state, { previousIndex, currentIndex }): EditState => {
            const teasers = [...state.aboutUsTeasersDraft];
            moveItemInArray(teasers, previousIndex, currentIndex);
            return {
                ...state,
                aboutUsTeasersDraft: teasers,
            };
        }),
        on(
            actions.loadEmployees,
            (state): EditState => ({
                ...state,
                employees: [],
            }),
        ),
        on(
            actions.loadEmployeesSuccess,
            (state, { employees }): EditState => ({
                ...state,
                employees,
            }),
        ),
        on(actions.updateEmployeeOrder, (state, { previousIndex, currentIndex }): EditState => {
            const employees = [...state.employees];
            moveItemInArray(employees, previousIndex, currentIndex);
            return {
                ...state,
                employees,
            };
        }),
        on(actions.updateEmployee, (state, { id, index, employee }): EditState => {
            if (id === -1 && index === -1) {
                return {
                    ...state,
                    employees: [...state.employees, employee as ApiTypes.EditEmployee],
                };
            }
            let employees = [...state.employees];
            const idx = id === -1 ? index : employees.findIndex((e) => e.id === id);
            if (idx === -1) {
                return state;
            }
            if (employee.homeContact || employee.aboutUsContact || employee.serviceContact) {
                employees = employees.map((e) => ({
                    ...e,
                    homeContact: employee.homeContact ? false : e.homeContact,
                    aboutUsContact: employee.aboutUsContact ? false : e.aboutUsContact,
                    serviceContact: employee.serviceContact ? false : e.serviceContact,
                }));
            }
            employees[idx] = { ...employees[idx], ...employee };
            return {
                ...state,
                employees,
            };
        }),
        on(actions.deleteEmployee, (state, { index }): EditState => {
            const employees = [...state.employees];
            employees.splice(index, 1);
            return {
                ...state,
                employees,
            };
        }),
        on(
            actions.loadDealerCampaigns,
            (state): EditState => ({
                ...state,
                dealerCampaigns: null,
            }),
        ),
        on(
            actions.loadDealerCampaignsSuccess,
            (state, { dealerCampaigns }): EditState => ({
                ...state,
                dealerCampaigns,
            }),
        ),
        on(
            actions.loadDealerCampaignsFailure,
            (state): EditState => ({
                ...state,
                dealerCampaigns: false,
            }),
        ),
        on(
            actions.loadDealerCampaignDetail,
            (state): EditState => ({
                ...state,
                dealerCampaign: null,
            }),
        ),
        on(
            actions.loadDealerCampaignDetailSuccess,
            (state, { campaign }): EditState => ({
                ...state,
                dealerCampaign: campaign,
            }),
        ),
        on(
            actions.loadDealerCampaignDetailFailure,
            (state): EditState => ({
                ...state,
                dealerCampaign: false,
            }),
        ),
        on(
            actions.upsertDealerCampaign,
            (state): EditState => ({
                ...state,
                dealerCampaign: null,
            }),
        ),
        on(
            actions.upsertDealerCampaignSuccess,
            (state, { campaign }): EditState => ({
                ...state,
                dealerCampaign: campaign,
            }),
        ),
        on(
            actions.loadTextListBullets,
            (state): EditState => ({
                ...state,
                textListBullets: [],
            }),
        ),
        on(
            actions.loadTextListBulletsSuccess,
            (state, { bullets }): EditState => ({
                ...state,
                textListBullets: bullets,
            }),
        ),
        on(
            actions.loadTextListBulletsFailure,
            (state): EditState => ({
                ...state,
                textListBullets: [],
            }),
        ),
        on(
            actions.loadServiceList,
            (state): EditState => ({
                ...state,
                services: [],
            }),
        ),
        on(
            actions.loadServiceListSuccess,
            (state, { services }): EditState => ({
                ...state,
                services: services,
            }),
        ),
        on(
            actions.loadServiceListFailure,
            (state): EditState => ({
                ...state,
                services: [],
            }),
        ),
        on(
            actions.upsertDealerCampaignFailure,
            (state): EditState => ({
                ...state,
            }),
        ),
        on(
            actions.loadHomeTeaserList,
            (state): EditState => ({
                ...state,
                teasers: [],
            }),
        ),
        on(
            actions.loadHomeTeaserListSuccess,
            (state, { teasers }): EditState => ({
                ...state,
                teasers: teasers,
            }),
        ),
        on(
            actions.loadHomeTeaserListFailure,
            (state): EditState => ({
                ...state,
                teasers: [],
            }),
        ),
    ),
    extraSelectors: (baseSelectors) => {
        const selectHomeTeasers = createSelector(HomeFeature.state.selectHomeTeasers, (content) => content);
        const selectHomeTheme = createSelector(HomeFeature.state.selectLoadedHome, (home) => home.theme);
        const selectAboutUsHeadlineHeadline = createSelector(
            AboutUsFeature.state.selectLoadedAboutUsContent,
            (content) => content.headline,
        );
        const selectAboutUsHeadlineCopy = createSelector(
            AboutUsFeature.state.selectLoadedAboutUsContent,
            (content) => content.heroCopy,
        );
        const selectAboutUsHeroImage = createSelector(
            AboutUsFeature.state.selectLoadedAboutUsContent,
            (content) => content.heroImage,
        );
        const selectShopSubText = createSelector(
            ShopPageFeature.state.selectLoadedContent,
            (content) => content.subText,
        );
        const selectImprintCopy = createSelector(
            ImprintPageFeature.state.selectLoadedContent,
            (content) => content.copy,
        );
        const selectAboutUsTeasers = createSelector(AboutUsFeature.state.selectTeasers, (teasers) => teasers);
        const selectServiceTextHeadline = createSelector(
            ServicesFeature.state.selectLoadedServicesHero,
            (content) => content.serviceTextHeadline,
        );
        const selectServiceText = createSelector(
            ServicesFeature.state.selectLoadedServicesHero,
            (content) => content.serviceText,
        );
        const selectServiceHighlights = createSelector(
            ServicesFeature.state.selectGeneralServices,
            (content) => content,
        );
        const selectAboutUsJobOfferBtnLink = createSelector(AboutUsFeature.state.selectJobOfferContent, (content) =>
            content && content.jobOfferBtn.link.startsWith('http') ? content.jobOfferBtn.link : '',
        );

        const selectAboutUsJobOfferHeroImage = createSelector(AboutUsFeature.state.selectJobOfferContent, (content) =>
            content && content.heroImage ? content.heroImage : '',
        );
        const selectAboutUsJobOfferBenefits = createSelector(AboutUsFeature.state.selectJobOfferContent, (content) =>
            content && content.benefits ? content.benefits : '',
        );

        const selectPrivacyDataProtection = createSelector(
            PrivacyFeature.state.selectLoadedContent,
            (content) => content.dataProtection,
        );

        const selectIsLoadingDealerCampaigns = createSelector(
            baseSelectors.selectDealerCampaigns,
            (campaigns) => campaigns === null,
        );
        const selectLoadedDealerCampaigns = createSelector(
            baseSelectors.selectDealerCampaigns,
            (campaigns) => campaigns as Loaded<ApiTypes.DealerCampaignEdit[]>,
        );
        const selectIsLoadingDealerCampaign = createSelector(
            baseSelectors.selectDealerCampaign,
            (campaign) => campaign === null,
        );
        const selectLoadedDealerCampaign = createSelector(
            baseSelectors.selectDealerCampaign,
            (campaign) => campaign as Loaded<ApiTypes.DealerCampaignEdit>,
        );
        const selectBusinessCustomerBulletIds = createSelector(
            BusinessCustomerFeature.state.selectContent,
            (content) => (content ? content.heroBulletList.map((item) => item.id) : []),
        );
        const selectServiceBulletIds = createSelector(ServicesFeature.state.selectServicesHero, (content) =>
            content ? content.bullets.map((item) => item.id) : [],
        );

        const selectHomeImageTextContent = createSelector(
            HomeFeature.state.selectHomeImageTextContent,
            (content) => content,
        );

        return {
            ...baseSelectors,
            selectHomeTeasers,
            selectHomeTheme,
            selectAboutUsHeadlineHeadline,
            selectAboutUsHeadlineCopy,
            selectAboutUsHeroImage,
            selectImprintCopy,
            selectAboutUsTeasers,
            selectServiceTextHeadline,
            selectServiceText,
            selectServiceBulletIds,
            selectServiceHighlights,
            selectAboutUsJobOfferBtnLink,
            selectAboutUsJobOfferHeroImage,
            selectAboutUsJobOfferBenefits,
            selectPrivacyDataProtection,
            selectIsLoadingDealerCampaigns,
            selectLoadedDealerCampaigns,
            selectIsLoadingDealerCampaign,
            selectLoadedDealerCampaign,
            selectBusinessCustomerBulletIds,
            selectHomeImageTextContent,
            selectShopSubText,
        };
    },
});
